import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Alert, Autocomplete, Box, TextField } from "@mui/material";
import { useState } from "react";
import UiButton from "components/base/UiButton";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import { PRODUCTS, PROPOSAL, SAVE_ITEM } from "./api";
import { Worksheet, worksheetInput } from "./calculator";

type Product = {
  id: string;
  definition: Worksheet;
  organizationProductType?: { id: string; name: string } | null;
};
type Props = { open: boolean; onClose: () => void; stageId: string };

export default function AddWorksheetProductDrawer({ open, onClose, stageId }: Props) {
  const client = useApolloClient();
  const { data, loading, error, refetch } = useQuery(PRODUCTS, {
    skip: !open,
    fetchPolicy: "network-only",
  });
  const [save] = useMutation(SAVE_ITEM);
  const [selected, setSelected] = useState<Product | null>(null);
  const [inserted, setInserted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const close = () => {
    if (busy) return;
    setSelected(null);
    setInserted(false);
    setFailure("");
    onClose();
  };
  const products: Product[] = [...(data?.worksheetProducts ?? [])]
    .filter((product) => product.organizationProductType)
    .sort(
      (a, b) =>
        (a.organizationProductType?.name ?? "").localeCompare(
          b.organizationProductType?.name ?? ""
        ) || a.definition.name.localeCompare(b.definition.name)
    );
  const current = products.find((product) => product.id === selected?.id) ?? null;
  const add = async () => {
    if (!current || busy) return;
    setBusy(true);
    setFailure("");
    try {
      if (!inserted) {
        await save({ variables: { stageId, input: worksheetInput(current.definition) } });
        setInserted(true);
      }
    } catch (e) {
      setFailure((e as Error).message);
      setBusy(false);
      return;
    }
    try {
      await client.refetchQueries({ include: [PROPOSAL, "customGetProposal"] });
      setSelected(null);
      setInserted(false);
      onClose();
    } catch (e) {
      setFailure(
        `Product added, but the proposal could not refresh. Retry refreshing: ${
          (e as Error).message
        }`
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <UiSideModal open={open} onClose={close} title="Add Product" width="598px">
      <Box sx={{ p: "24px", display: "flex", flexDirection: "column", gap: 2 }}>
        <UiTypography uiVariant="heading-sm">Product</UiTypography>
        {error && (
          <Alert
            severity="error"
            action={
              <UiButton uiVariant="outlined-small" onClick={() => refetch()}>
                Retry
              </UiButton>
            }
          >
            Saved products could not load: {error.message}
          </Alert>
        )}
        {!loading && !error && products.length === 0 && (
          <Alert severity="info">
            No saved Worksheet products yet. Use Add Blank Product to create one, then Save as
            product to reuse it.
          </Alert>
        )}
        <Autocomplete
          options={products}
          groupBy={(product) => product.organizationProductType?.name ?? ""}
          value={current}
          onChange={(_, product) => setSelected(product)}
          getOptionLabel={(product) => product.definition.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          loading={loading}
          disabled={busy || inserted || !!error}
          renderInput={(params) => <TextField {...params} label="Select product" size="small" />}
        />
        {failure && <Alert severity="error">{failure}</Alert>}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <UiButton
            uiVariant="primary-blue"
            disabled={!current || loading || busy || !!error}
            onClick={add}
          >
            {busy
              ? inserted
                ? "Refreshing…"
                : "Adding…"
              : inserted
              ? "Refresh proposal"
              : "Add Product"}
          </UiButton>
        </Box>
      </Box>
    </UiSideModal>
  );
}
